import BusinessList from '@/components/business/BusinessList';
import DownloadApp from '@/components/shared/DownloadApp';
import { ENV } from '@/core/config/env';
import { GET_BUSINESS_LIST } from '@/core/graphql/queries/getBusinessList';
import { BusinessItem } from '@/core/models/businesses/businessList';
import { SearchBusinessRequest } from '@/core/models/businesses/businessRequest';
import { initializeApollo } from '@/lib/apolloClient';
import { slugify } from '@/lib/slugify';
import { getString } from '@/lib/stringUtils';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';

interface Props {
    businesses: BusinessItem[];
    pagination: {
        count: number;
        currentPage: number;
        totalPages: number;
    };
}

const quickCities = [
    { name: 'Johannesburg', province: 'gauteng' },
    { name: 'Pretoria', province: 'gauteng' },
    { name: 'Cape Town', province: 'western-cape' },
    { name: 'Durban', province: 'kwazulu-natal' },
    { name: 'Gqeberha', province: 'eastern-cape' },
    { name: 'Bloemfontein', province: 'free-state' },
];

const cityToProvinceMap: Record<string, string> = {
    Johannesburg: 'gauteng',
    Pretoria: 'gauteng',
    'Cape Town': 'western-cape',
    Durban: 'kwazulu-natal',
    Gqeberha: 'eastern-cape',
    Bloemfontein: 'free-state',
};

const quickProvinces = [
    'Gauteng',
    'Western Cape',
    'KwaZulu-Natal',
    'Eastern Cape',
    'Free State',
    'Mpumalanga',
    'Limpopo',
    'North West',
    'Northern Cape',
];

export default function MidasNearMePage({ businesses, pagination }: Props) {
    const router = useRouter();
    const { query } = router;

    const searchParams = {
        category: getString(query.category),
        province: getString(query.province),
        city: getString(query.city),
        suburb: getString(query.suburb),
        text: '',
        page: query.page ? parseInt(query.page as string, 10) : 1,
    };

    const toTitleCase = (value: string) =>
        value
            .split('-')
            .filter(Boolean)
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(' ');

    const provinceName = searchParams.province ? toTitleCase(searchParams.province) : '';
    const cityName = searchParams.city ? toTitleCase(searchParams.city) : '';
    const suburbName = searchParams.suburb ? toTitleCase(searchParams.suburb) : '';
    const hasLocationFilter = Boolean(provinceName || cityName || suburbName);
    const locationLabel = suburbName
        ? [suburbName, cityName, provinceName].filter(Boolean).join(', ')
        : cityName
          ? [cityName, provinceName].filter(Boolean).join(', ')
          : provinceName;

    const locationSuffix = hasLocationFilter
        ? `in ${locationLabel}`
        : 'in South Africa';

    const titleLocationLabel = hasLocationFilter
        ? `in ${locationLabel}`
        : 'Near Me';

    const pageTitle = hasLocationFilter
        ? `Midas ${locationLabel} | Midas Auto Service Centre & Fitment Centre`
        : 'Midas Near Me | Midas Auto Service Centre & Fitment Centre';

    const pageDescription = hasLocationFilter
        ? `Find your nearest Midas auto service centre in ${locationLabel}. Expert exhaust, brakes, suspension, tyres, shocks, and full vehicle services. Trusted since 1956. Book your service today.`
        : 'Find your nearest Midas auto service centre. Expert exhaust, brakes, suspension, tyres, shocks, and full vehicle services across South Africa. Trusted since 1956. Book your service today.';

    const baseUrl = `${ENV.DOMAIN_URL}/midas-near-me`;
    const canonicalParams = new URLSearchParams();
    if (searchParams.province) canonicalParams.set('province', searchParams.province);
    if (searchParams.city) canonicalParams.set('city', searchParams.city);
    if (searchParams.suburb) canonicalParams.set('suburb', searchParams.suburb);
    if (searchParams.page > 1) canonicalParams.set('page', String(searchParams.page));
    const canonicalUrl = canonicalParams.toString()
        ? `${baseUrl}?${canonicalParams.toString()}`
        : baseUrl;

    const suburbLinksByCity: Record<string, string[]> = {
        Johannesburg: ['Sandton', 'Randburg', 'Roodepoort', 'Soweto', 'Midrand'],
        Pretoria: ['Centurion', 'Hatfield', 'Menlyn', 'Brooklyn', 'Silverton'],
        'Cape Town': ['Bellville', 'Century City', 'Milnerton', 'Woodstock', 'Somerset West'],
        Durban: ['Umhlanga', 'Pinetown', 'Westville', 'Berea', 'Amanzimtoti'],
        Gqeberha: ['Walmer', 'Newton Park', 'Summerstrand', 'Korsten', 'Uitenhage'],
        Bloemfontein: ['Universitas', 'Langenhoven Park', 'Brandwag', 'Westdene', 'Hilton'],
    };

    const suburbLinks = cityName && suburbLinksByCity[cityName] ? suburbLinksByCity[cityName] : [];
    const locationKeywords = hasLocationFilter ? locationLabel : 'South Africa';
    const keywordList = [
        `midas ${locationKeywords}`,
        `midas near me ${locationKeywords}`,
        `midas auto service centre ${locationKeywords}`,
        `midas fitment centre ${locationKeywords}`,
        `midas exhaust fitment ${locationKeywords}`,
        `midas brakes and suspension ${locationKeywords}`,
        `midas tyres ${locationKeywords}`,
    ].join(', ');

    const faqItems = [
        {
            question: `How do I find a Midas ${locationSuffix}?`,
            answer: hasLocationFilter
                ? `You are filtering for ${locationLabel}. Use the province, city, or suburb filters to find the closest Midas centres, or clear filters to see all Midas locations.`
                : 'Use the province, city, or suburb filters to find your nearest Midas centre. You can also browse Midas near me to see all locations across South Africa.',
        },
        {
            question: `What services does Midas offer ${locationSuffix}?`,
            answer: 'Midas specializes in exhaust systems, brakes, suspension, shock absorbers, tyres, batteries, wheel alignment, and full vehicle services. All Midas centres offer professional fitment and expert advice.',
        },
        {
            question: 'Do I need to book an appointment at Midas?',
            answer: 'While walk-ins are welcome at most Midas centres, booking an appointment is recommended to minimize wait times, especially for comprehensive services like brake replacements or suspension work.',
        },
        {
            question: 'Does Midas offer warranties on their services?',
            answer: 'Yes, Midas provides warranties on parts and workmanship. Exhaust systems typically come with a lifetime warranty, while other services have varying warranty periods. Check with your local Midas centre for specific details.',
        },
        {
            question: 'What makes Midas different from other service centres?',
            answer: 'Midas has been trusted since 1956 and specializes in under-car services. Their technicians are trained experts in exhaust, brakes, and suspension systems, offering quality fitments with nationwide warranty support.',
        },
    ];

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content={keywordList} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={pageTitle} />
                <meta name="twitter:description" content={pageDescription} />
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            '@context': 'https://schema.org',
                            '@graph': [
                                {
                                    '@type': 'LocalBusiness',
                                    '@id': canonicalUrl,
                                    name: `Midas ${titleLocationLabel}`,
                                    description: pageDescription,
                                    url: canonicalUrl,
                                    areaServed: locationKeywords,
                                    knowsAbout: [
                                        'Exhaust Systems',
                                        'Brake Services',
                                        'Suspension Repair',
                                        'Shock Absorbers',
                                        'Tyre Fitment',
                                        'Wheel Alignment',
                                    ],
                                },
                                {
                                    '@type': 'FAQPage',
                                    mainEntity: faqItems.map((item) => ({
                                        '@type': 'Question',
                                        name: item.question,
                                        acceptedAnswer: {
                                            '@type': 'Answer',
                                            text: item.answer,
                                        },
                                    })),
                                },
                            ],
                        }),
                    }}
                />
            </Head>
            <section className="relative overflow-hidden bg-[#0f1512] text-white">
                <div
                    className="absolute inset-0 opacity-35"
                    style={{
                        backgroundImage: "radial-gradient(circle at 15% 20%, rgba(234, 179, 8, 0.35), transparent 45%), radial-gradient(circle at 80% 10%, rgba(202, 138, 4, 0.3), transparent 40%), radial-gradient(circle at 65% 85%, rgba(250, 204, 21, 0.25), transparent 45%)",
                    }}
                />
                <div className="container relative py-12 md:py-16">
                    <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
                        <div className="lg:col-span-7">
                            <p className="text-xs uppercase tracking-[0.3em] text-yellow-200">Midas Auto Service Centre</p>
                            <h1 className="mt-3 text-3xl md:text-5xl font-bold leading-tight">
                                Midas {titleLocationLabel}
                            </h1>
                            <p className="mt-4 text-base md:text-lg text-yellow-100">
                                Find your nearest Midas auto service centre. Expert exhaust, brakes, suspension, tyres, 
                                and shocks. Trusted since 1956 with lifetime warranties.
                            </p>
                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                <Link
                                    href="#business-filters"
                                    className="bg-white text-slate-900 px-5 py-2.5 rounded-md text-sm font-semibold shadow"
                                >
                                    Find your Midas
                                </Link>
                                <Link
                                    href="/midas-near-me"
                                    className="bg-yellow-500 text-slate-900 px-5 py-2.5 rounded-md text-sm font-semibold shadow"
                                >
                                    All Midas locations
                                </Link>
                                <span className="text-xs text-yellow-100">Quick city shortcuts below.</span>
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <div className="rounded-2xl border border-white/20 bg-white/10 p-5 backdrop-blur">
                                <div className="flex items-center justify-between">
                                    <p className="text-sm font-semibold text-yellow-100">Popular Midas locations</p>
                                    <span className="rounded-full bg-yellow-500/20 px-2.5 py-1 text-xs text-yellow-100">
                                        Trusted
                                    </span>
                                </div>
                                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                                    <Link
                                        href="/midas-near-me/midas/gauteng/johannesburg"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-yellow-300"
                                    >
                                        Midas Johannesburg
                                    </Link>
                                    <Link
                                        href="/midas-near-me/midas/western-cape/cape-town"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-yellow-300"
                                    >
                                        Midas Cape Town
                                    </Link>
                                    <Link
                                        href="/midas-near-me/midas/kwazulu-natal/durban"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-yellow-300"
                                    >
                                        Midas Durban
                                    </Link>
                                    <Link
                                        href="/midas-near-me/midas/gauteng/pretoria"
                                        className="rounded-lg border border-white/20 px-4 py-3 text-sm text-white hover:border-yellow-300"
                                    >
                                        Midas Pretoria
                                    </Link>
                                </div>
                                <div className="mt-5 grid gap-3 sm:grid-cols-3 text-xs text-yellow-100">
                                    <div className="rounded-lg border border-white/10 px-3 py-2">Exhaust specialists</div>
                                    <div className="rounded-lg border border-white/10 px-3 py-2">Brake experts</div>
                                    <div className="rounded-lg border border-white/10 px-3 py-2">Lifetime warranty</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10">
                <div className="grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-8">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-2xl font-semibold text-slate-900">Your Trusted Midas Auto Service Centre</h2>
                            <p className="mt-3 text-sm text-slate-600">
                                Looking for Midas {locationSuffix}? CarSparesEzyFind helps you locate
                                Midas auto service centres and fitment centres across South Africa. Since 1956, Midas has been 
                                the trusted name in exhaust systems, brakes, suspension, and shock absorbers. Use the filters 
                                to find your nearest Midas centre by province, city, or suburb.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                Whether you need a Midas near me in Sandton, a Midas fitment centre in
                                Roodepoort, or Midas auto service in Randburg, this page helps you find the most
                                convenient location. Midas centres offer professional fitment, expert diagnostics, and 
                                quality parts with industry-leading warranties. All technicians are trained specialists 
                                in under-car services.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                Popular searches include Midas Johannesburg Gauteng, Midas service centre Johannesburg,
                                Midas fitment centre Cape Town, and Midas exhaust Durban.
                                Services include exhaust system replacement and repair, brake pads and disc replacement, 
                                suspension work, shock absorber fitment, tyre sales and fitment, wheel alignment, and 
                                battery replacement. Book your service or visit your nearest Midas for expert advice.
                            </p>
                            <p className="mt-3 text-sm text-slate-600">
                                Midas centres are independently owned but follow strict quality standards. Most locations 
                                offer same-day service for common repairs. Check with your local Midas centre for specific 
                                services, operating hours, and promotional offers. Many Midas centres also offer free 
                                safety inspections.
                            </p>
                        </div>
                    </div>
                    <div className="lg:col-span-4">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h3 className="text-lg font-semibold text-slate-900">Midas Services</h3>
                            <ul className="mt-4 space-y-2 text-sm text-slate-600">
                                <li>Exhaust systems & mufflers</li>
                                <li>Brakes (pads, discs, drums)</li>
                                <li>Suspension & steering</li>
                                <li>Shock absorbers & struts</li>
                                <li>Tyres & wheel alignment</li>
                                <li>Batteries & electrical</li>
                            </ul>
                            <h3 className="mt-6 text-lg font-semibold text-slate-900">Why Choose Midas</h3>
                            <p className="mt-3 text-sm text-slate-600">
                                Midas has over 65 years of experience with skilled technicians, lifetime warranties on 
                                exhaust systems, competitive pricing, and a nationwide network of service centres.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10">
                <div className="grid gap-6 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <h2 className="text-lg font-semibold text-slate-900">Find Midas by city</h2>
                                    <p className="text-sm text-slate-600 mt-1">
                                        Fast links to Midas centres in major cities and metro areas.
                                    </p>
                                </div>
                                <Link
                                    href="/midas-near-me"
                                    className="text-sm font-semibold text-yellow-700"
                                >
                                    View all locations
                                </Link>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {quickCities.map((city) => (
                                    <Link
                                        key={city.name}
                                        href={`/midas-near-me/midas/${city.province}/${slugify(city.name)}`}
                                        className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-yellow-400 hover:text-yellow-700"
                                    >
                                        {city.name}
                                    </Link>
                                ))}
                            </div>
                            <div className="mt-6 grid gap-3 md:grid-cols-3">
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 1</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Choose location</p>
                                    <p className="mt-1 text-xs text-slate-600">Select your province, city, or suburb.</p>
                                </div>
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 2</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Find nearest Midas</p>
                                    <p className="mt-1 text-xs text-slate-600">View closest service centres.</p>
                                </div>
                                <div className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Step 3</p>
                                    <p className="mt-2 text-sm font-semibold text-slate-900">Book service</p>
                                    <p className="mt-1 text-xs text-slate-600">Contact or visit your local Midas.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-slate-900">Browse by province</h2>
                            <p className="text-sm text-slate-600 mt-1">
                                Filter by province, then drill down to city and suburb.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {quickProvinces.map((province) => (
                                    <Link
                                        key={province}
                                        href={`/midas-near-me/midas/${slugify(province)}`}
                                        className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-yellow-400 hover:text-yellow-700"
                                    >
                                        {province}
                                    </Link>
                                ))}
                            </div>
                        </div>
                        <div className="mt-6 rounded-2xl border border-yellow-100 bg-yellow-50 p-5">
                            <h3 className="text-sm font-semibold text-slate-900">Service tips</h3>
                            <ul className="mt-2 space-y-2 text-sm text-slate-700">
                                <li>Book appointments to avoid wait times.</li>
                                <li>Ask about lifetime warranties on exhaust systems.</li>
                                <li>Many Midas centres offer free safety inspections.</li>
                                <li>Check for seasonal promotions and service specials.</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            <section className="container my-10" aria-labelledby="suburb-links-heading">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <h2 id="suburb-links-heading" className="text-xl font-semibold text-slate-900">
                        {cityName
                            ? `Midas Centres by Suburb in ${cityName}`
                            : 'Midas Centres by City in South Africa'}
                    </h2>
                    <p className="mt-2 text-sm text-slate-600">
                        {cityName
                            ? `Explore Midas centres in ${cityName} suburbs to find your nearest service centre.`
                            : 'Jump to popular cities and metros to find Midas centres near you.'}
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {cityName && suburbLinks.length > 0
                            ? suburbLinks.map((suburb) => (
                                  <Link
                                      key={suburb}
                                      href={`/midas-near-me?city=${slugify(cityName)}&suburb=${slugify(suburb)}`}
                                      className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-yellow-400 hover:text-yellow-700"
                                  >
                                      {`Midas ${suburb}`}
                                  </Link>
                              ))
                            : quickCities.map((city) => (
                                  <Link
                                      key={city.name}
                                      href={`/midas-near-me/midas/${city.province}/${slugify(city.name)}`}
                                      className="px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-700 hover:border-yellow-400 hover:text-yellow-700"
                                  >
                                      {`Midas ${city.name}`}
                                  </Link>
                              ))}
                    </div>
                </div>
            </section>

            <div className="my-10">
                <div className="container" id="business-filters">
                    <BusinessList
                        searchParams={searchParams}
                        defaultCategoryIds="5017"
                        initialBusinesses={businesses}
                        initialPagination={pagination}
                    />
                </div>
            </div>

            <section className="container my-12">
                <div className="grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-5">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <p className="text-xs uppercase tracking-[0.25em] text-yellow-700">Midas Directory</p>
                            <h2 className="mt-3 text-2xl font-semibold text-slate-900">
                                Midas auto service centres {locationSuffix}
                            </h2>
                            <p className="mt-3 text-sm text-slate-600">
                                Use this page to find Midas near you, or drill into a specific province,
                                city, or suburb. Results update instantly as you apply filters.
                            </p>
                            <p className="mt-4 text-sm text-slate-600">
                                Browse Midas locations, service centres, and fitment centres in
                                one place. Compare locations, services, and contact details side-by-side.
                            </p>
                            <div className="mt-5 rounded-xl bg-slate-50 p-4 text-xs text-slate-600">
                                {hasLocationFilter
                                    ? `Showing Midas centres for ${locationLabel}. Change province, city, or suburb to explore nearby areas.`
                                    : 'Tip: Start with a province, then narrow by city or suburb for your nearest Midas centre.'}
                            </div>
                        </div>
                    </div>
                    <div className="lg:col-span-7">
                        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                            <h2 className="text-lg font-semibold text-slate-900">
                                Frequently Asked Questions (Midas Services SEO)
                            </h2>
                            <div className="mt-4 space-y-4">
                                {faqItems.map((item) => (
                                    <div key={item.question} className="rounded-xl border border-slate-100 bg-slate-50 p-4">
                                        <h3 className="text-sm font-semibold text-slate-900">{item.question}</h3>
                                        <p className="mt-2 text-sm text-slate-600">{item.answer}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <DownloadApp />
        </>
    );
}

export const getStaticProps = async () => {
    const apolloClient = initializeApollo();
    const businessRequest: SearchBusinessRequest = {
        companyId: null,
        companyName: '',
        franchiseeId: null,
        statusIds: '1',
        categoryIds: '5017',
        provinceIds: '',
        cityIds: '',
        suburbIds: '',
        page: 1,
        size: 12,
    };

    const { data: businessData } = await apolloClient.query({
        query: GET_BUSINESS_LIST,
        variables: businessRequest,
    });

    const isStaticExport = process.env.BUILD_MODE === 'export';
    
    return {
        props: {
            businesses: businessData?.getBusinessList?.result || [],
            pagination: {
                count: businessData?.getBusinessList?.count ?? 0,
                currentPage: businessData?.getBusinessList?.currentPage ?? 1,
                totalPages: businessData?.getBusinessList?.totalPages ?? 0,
            },
        },
        ...(!isStaticExport && { revalidate: 3600 }),
    };
};
